import { clear, createStore, delMany, entries, keys, setMany } from 'idb-keyval';
import { idle } from '@lib/idle';

// IndexedDB-persisted store snapshots for warm boot: on startup, stores are seeded from
// here and render immediately while a normal full reload revalidates everything in the
// background (see store/warm-boot.ts and app.js). Every operation swallows errors —
// any failure just means a cold boot, never a broken one.

export type Snapshot = { data: unknown; savedAt: number; schemaHash: string };

// A snapshot this old is dropped at read time. Purely a hygiene backstop (e.g. returning
// from vacation) — ordinary staleness is handled by the background revalidation.
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

// Dedicated database so purges can't touch unrelated idb-keyval users.
const idbStore = typeof indexedDB === 'undefined' ? undefined : createStore('plm-snapshots', 'snapshots');

const keyPrefix = (userId: string, brandId: number) => `plm:${userId}:${brandId}:`;

export async function readSnapshots(userId: string, brandId: number): Promise<Record<string, Snapshot>> {
  if (!idbStore) return {};
  try {
    const prefix = keyPrefix(userId, brandId);
    const now = Date.now();
    const result: Record<string, Snapshot> = {};
    for (const [key, value] of await entries<string, Snapshot>(idbStore)) {
      if (typeof key !== 'string' || !key.startsWith(prefix)) continue;
      if (!value || typeof value.savedAt !== 'number' || now - value.savedAt > MAX_AGE_MS) continue;
      result[key.slice(prefix.length)] = value;
    }
    return result;
  } catch {
    return {};
  }
}

// Writes are queued and flushed as one batched transaction during idle time, so the
// ~60 persists after a boot's revalidation don't hammer IndexedDB individually. The
// debounce also paces bursts from mutation-driven persists (e.g. table scrolling
// filling the article caches) — the queued data is a live reference and the structured
// clone happens at flush time, so the last flush always captures the latest state.
const pendingWrites = new Map<string, Snapshot>();
let flushScheduled = false;
const FLUSH_DEBOUNCE_MS = 2000;

export function queueSnapshotWrite(
  userId: string,
  brandId: number,
  storeName: string,
  data: unknown,
  schemaHash: string
) {
  if (!idbStore) return;
  pendingWrites.set(keyPrefix(userId, brandId) + storeName, { data, savedAt: Date.now(), schemaHash });
  if (flushScheduled) return;
  flushScheduled = true;
  setTimeout(() => idle(() => void flush()), FLUSH_DEBOUNCE_MS);
}

async function flush() {
  flushScheduled = false;
  const batch = [...pendingWrites.entries()];
  pendingWrites.clear();
  try {
    await setMany(batch, idbStore);
  } catch {
    // e.g. non-cloneable value or quota exceeded — those stores simply cold-load next boot
  }
}

export async function purgeBrand(userId: string, brandId: number) {
  if (!idbStore) return;
  try {
    const prefix = keyPrefix(userId, brandId);
    const allKeys = await keys<string>(idbStore);
    await delMany(
      allKeys.filter((key) => typeof key === 'string' && key.startsWith(prefix)),
      idbStore
    );
  } catch {
    /* best effort */
  }
}

export async function purgeAll() {
  if (!idbStore) return;
  try {
    await clear(idbStore);
  } catch {
    /* best effort */
  }
}
